import { db, makeId } from "./db";
import type {
  ExerciseDefinition,
  ProgramImportMetadata,
  WorkoutProgramFile,
  WorkoutTemplate,
  WorkoutTemplateExercise,
} from "./types";

export interface ProgramDiffLine {
  status: "same" | "changed" | "added" | "removed";
  session: string;
  exercise: string;
  before?: string;
  after?: string;
}

export interface ImportIdentityCandidate {
  key: string;
  incomingName: string;
  sourceExerciseId?: string;
  candidates: Array<{ id: string; name: string; reason: "source_id" | "exact_name" }>;
}

const isNumber = (value: unknown) => typeof value === "number" && Number.isFinite(value);
const hasName = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;

export function validateProgramFile(value: unknown): asserts value is WorkoutProgramFile {
  if (!value || typeof value !== "object") throw new Error("Le fichier doit contenir un objet JSON.");
  const file = value as Partial<WorkoutProgramFile>;
  if (file.format !== "workout-program") throw new Error("Format attendu : workout-program.");
  if (file.schemaVersion !== 1) throw new Error("Version de programme non prise en charge.");
  if (!hasName(file.program?.name) || !Array.isArray(file.program.sessions) || file.program.sessions.length === 0) {
    throw new Error("Le programme doit avoir un nom et au moins une séance.");
  }
  const sessionNames = new Set<string>();
  for (const session of file.program.sessions) {
    if (!session || !hasName(session.name) || sessionNames.has(session.name.trim().toLocaleLowerCase("fr"))) throw new Error("Chaque séance doit avoir un nom unique.");
    sessionNames.add(session.name.trim().toLocaleLowerCase("fr"));
    if (!Array.isArray(session.exercises) || session.exercises.length === 0) throw new Error(`${session.name} ne contient aucun exercice.`);
    for (const exercise of session.exercises) {
      if (!exercise || !hasName(exercise.name)) throw new Error(`Un exercice de ${session.name} n’a pas de nom.`);
      if (!Number.isInteger(exercise.sets) || exercise.sets < 1 || exercise.sets > 20) throw new Error(`${exercise.name} : nombre de séries invalide.`);
      if (exercise.minSets != null && (!Number.isInteger(exercise.minSets) || exercise.minSets < 1 || exercise.minSets > exercise.sets)) throw new Error(`${exercise.name} : minimum de séries invalide.`);
      const hasReps = exercise.repRange && isNumber(exercise.repRange.min) && isNumber(exercise.repRange.max);
      const hasDuration = exercise.durationRange && isNumber(exercise.durationRange.min) && isNumber(exercise.durationRange.max);
      if (Boolean(hasReps) === Boolean(hasDuration)) throw new Error(`${exercise.name} : indiquez soit Reps, soit Durée.`);
      if (exercise.repRange != null && exercise.durationRange != null) throw new Error(`${exercise.name} : indiquez soit Reps, soit Durée.`);
      if (exercise.trackingMode != null && exercise.trackingMode !== (hasDuration ? "duration" : "reps")) throw new Error(`${exercise.name} : le suivi doit correspondre à la plage de répétitions ou de durée.`);
      const range = exercise.repRange ?? exercise.durationRange!;
      if (range.min < 0 || range.max < range.min) throw new Error(`${exercise.name} : plage invalide.`);
      if (!Number.isInteger(exercise.restSeconds) || exercise.restSeconds < 0) throw new Error(`${exercise.name} : repos invalide.`);
      if (exercise.load != null && (!isNumber(exercise.load) || exercise.load < 0)) throw new Error(`${exercise.name} : charge invalide.`);
      if (exercise.rir != null && (!isNumber(exercise.rir.min) || !isNumber(exercise.rir.max) || exercise.rir.min < 0 || exercise.rir.max < exercise.rir.min)) throw new Error(`${exercise.name} : RIR invalide.`);
      if (exercise.unit != null && !hasName(exercise.unit)) throw new Error(`${exercise.name} : unité invalide.`);
      if (exercise.progression?.increment != null && (!isNumber(exercise.progression.increment) || exercise.progression.increment < 0)) throw new Error(`${exercise.name} : incrément invalide.`);
      if (exercise.progression && !["double_progression", "manual", "duration", "discrete", "none"].includes(exercise.progression.type)) {
        throw new Error(`${exercise.name} : mode de progression inconnu.`);
      }
    }
  }
}

function parseRange(value: string, label: string) {
  const match = value.trim().match(/^(\d+(?:[.,]\d+)?)\s*-\s*(\d+(?:[.,]\d+)?)$/);
  if (!match) throw new Error(`${label} doit être une plage, par exemple 5-7.`);
  return { min: Number(match[1].replace(",", ".")), max: Number(match[2].replace(",", ".")) };
}

export function parseProgramMarkdown(markdown: string): WorkoutProgramFile {
  if (markdown.length > 500_000) throw new Error("Fichier trop volumineux (500 ko maximum).");
  const lines = markdown.replace(/\r/g, "").split("\n");
  const title = lines.find((line) => line.startsWith("# Programme:"));
  if (!title) throw new Error("En-tête attendu : # Programme: Nom.");
  const file: WorkoutProgramFile = {
    format: "workout-program",
    schemaVersion: 1,
    program: { name: title.slice(12).trim(), sessions: [] },
  };
  let session: WorkoutProgramFile["program"]["sessions"][number] | undefined;
  let exercise: WorkoutProgramFile["program"]["sessions"][number]["exercises"][number] | undefined;
  const knownKeys = new Set(["ID", "ID source", "Séries", "Reps", "Durée", "RIR", "Repos", "Unilatéral", "Par côté", "Charge", "Unité", "Suivi", "Progression", "Incrément", "Notes"]);
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || line.startsWith("# Programme:")) continue;
    if (line.startsWith("## Séance ")) {
      session = { name: line.slice(3).trim(), exercises: [] };
      file.program.sessions.push(session);
      exercise = undefined;
      continue;
    }
    if (line.startsWith("### ")) {
      if (!session) throw new Error("Un exercice apparaît avant sa séance.");
      exercise = { name: line.slice(4).trim(), sets: 0, restSeconds: -1 };
      session.exercises.push(exercise);
      continue;
    }
    if (!line.startsWith("- ") || !exercise) throw new Error(`Ligne non reconnue : ${line}`);
    const separator = line.indexOf(":");
    if (separator < 3) throw new Error(`Clé invalide : ${line}`);
    const key = line.slice(2, separator).trim();
    const value = line.slice(separator + 1).trim();
    if (!knownKeys.has(key)) throw new Error(`Clé Markdown inconnue : ${key}.`);
    if (key === "ID") exercise.exerciseId = value;
    if (key === "ID source") exercise.sourceExerciseId = value;
    if (key === "Séries") {
      const setRange = value.match(/^(\d+)\s*-\s*(\d+)$/);
      if (setRange) { exercise.minSets = Number(setRange[1]); exercise.sets = Number(setRange[2]); }
      else exercise.sets = Number(value);
    }
    if (key === "Reps") exercise.repRange = parseRange(value, "Reps");
    if (key === "Durée") exercise.durationRange = parseRange(value, "Durée");
    if (key === "RIR") exercise.rir = parseRange(value, "RIR");
    if (key === "Repos") exercise.restSeconds = Number(value);
    if (key === "Unilatéral") exercise.unilateral = value.toLowerCase() === "oui";
    if (key === "Par côté") exercise.perSide = value.toLowerCase() === "oui";
    if (key === "Charge") exercise.load = Number(value.replace(",", ".").split(" ")[0]);
    if (key === "Unité") exercise.unit = value;
    if (key === "Suivi") {
      if (!["reps", "duration"].includes(value)) throw new Error(`Suivi inconnu : ${value}.`);
      exercise.trackingMode = value as "reps" | "duration";
    }
    if (key === "Notes") exercise.notes = value;
    if (key === "Progression") {
      const modes: Record<string, WorkoutProgramFile["program"]["sessions"][number]["exercises"][number]["progression"]> = {
        double: { type: "double_progression" }, manuel: { type: "manual" }, durée: { type: "duration" }, palier: { type: "discrete" }, aucune: { type: "none" },
      };
      exercise.progression = modes[value.toLowerCase()];
      if (!exercise.progression) throw new Error(`Progression inconnue : ${value}.`);
    }
    if (key === "Incrément") {
      exercise.progression ??= { type: "double_progression" };
      exercise.progression.increment = Number(value.replace(",", ".").split(" ")[0]);
      exercise.progression.unit = value.split(" ")[1] || "kg";
    }
  }
  validateProgramFile(file);
  return file;
}

const prescriptionText = (exercise: { minSets?: number; sets: number; repRange?: { min: number; max: number }; durationRange?: { min: number; max: number } }) =>
  exercise.repRange
    ? `${exercise.minSets != null ? `${exercise.minSets}–` : ""}${exercise.sets} × ${exercise.repRange.min}–${exercise.repRange.max}`
    : `${exercise.minSets != null ? `${exercise.minSets}–` : ""}${exercise.sets} × ${exercise.durationRange?.min}–${exercise.durationRange?.max} s`;

export async function calculateProgramDiff(file: WorkoutProgramFile): Promise<ProgramDiffLine[]> {
  validateProgramFile(file);
  return file.program.sessions.flatMap((session) => session.exercises.map((exercise) => ({
    status: "added" as const,
    session: session.name,
    exercise: exercise.name,
    after: prescriptionText(exercise),
  })));
}

export async function findImportIdentityCandidates(file: WorkoutProgramFile): Promise<ImportIdentityCandidate[]> {
  validateProgramFile(file);
  const definitions = await db.exercises.toArray();
  return file.program.sessions.flatMap((session, sessionIndex) => session.exercises.flatMap((incoming, exerciseIndex) => {
    const sourceExerciseId = incoming.sourceExerciseId ?? incoming.exerciseId;
    const matches: ImportIdentityCandidate["candidates"] = [];
    for (const definition of definitions) {
      if (definition.trackingMode !== (incoming.trackingMode ?? (incoming.durationRange ? "duration" : "reps"))) continue;
      if (sourceExerciseId && (definition.id === sourceExerciseId || definition.sourceExerciseId === sourceExerciseId)) {
        matches.push({ id: definition.id, name: definition.name, reason: "source_id" });
        continue;
      }
      if (definition.name === incoming.name) matches.push({ id: definition.id, name: definition.name, reason: "exact_name" });
    }
    return matches.length ? [{ key: `${sessionIndex}:${exerciseIndex}`, incomingName: incoming.name, sourceExerciseId, candidates: matches }] : [];
  }));
}

export async function applyProgramImport(file: WorkoutProgramFile, fileName: string, identityResolutions: Record<string, string> = {}) {
  validateProgramFile(file);
  await db.transaction("rw", [db.exercises, db.templates, db.programs, db.imports, db.settings], async () => {
    const definitions = await db.exercises.toArray();
    const createdAt = Date.now();
    const newDefinitions: ExerciseDefinition[] = [];
    const templates: WorkoutTemplate[] = [];
    const programId = makeId("program");
    for (const [sessionIndex, session] of file.program.sessions.entries()) {
      const templateExercises: WorkoutTemplateExercise[] = [];
      for (const [position, incoming] of session.exercises.entries()) {
        const resolutionKey = `${sessionIndex}:${position}`;
        let definition = identityResolutions[resolutionKey]
          ? definitions.find((item) => item.id === identityResolutions[resolutionKey])
          : undefined;
        if (identityResolutions[resolutionKey] && !definition) throw new Error(`${incoming.name} : correspondance locale introuvable.`);
        if (definition && definition.trackingMode !== (incoming.trackingMode ?? (incoming.durationRange ? "duration" : "reps"))) throw new Error(`${incoming.name} : suivi incompatible avec l’exercice choisi.`);
        if (definition?.archived) { await db.exercises.update(definition.id, { archived: false, updatedAt: createdAt }); definition.archived = false; }
        if (!definition) {
          definition = {
            id: makeId("exercise"), sourceExerciseId: incoming.sourceExerciseId ?? incoming.exerciseId,
            name: incoming.name, archived: false,
            isUnilateral: Boolean(incoming.unilateral), perSide: Boolean(incoming.perSide),
            trackingMode: incoming.trackingMode ?? (incoming.durationRange ? "duration" : "reps"),
            progressionMode: incoming.progression?.type ?? "manual", createdAt, updatedAt: createdAt,
          };
          newDefinitions.push(definition);
          definitions.push(definition);
        }
        templateExercises.push({
          id: makeId("template_exercise"), exerciseDefinitionId: definition.id, minSetCount: incoming.minSets, setCount: incoming.sets,
          minReps: incoming.repRange?.min, maxReps: incoming.repRange?.max,
          minDuration: incoming.durationRange?.min, maxDuration: incoming.durationRange?.max,
          targetRirMin: incoming.rir?.min, targetRirMax: incoming.rir?.max,
          restSeconds: incoming.restSeconds, load: incoming.load ?? 0, unit: incoming.unit ?? "kg",
          loadIncrement: incoming.progression?.increment ?? 0, discreteSteps: incoming.progression?.steps,
          discreteStepIndex: incoming.progression?.currentStep, notes: incoming.notes, position,
        });
      }
      templates.push({ id: makeId("template"), programId, name: session.name, exercises: templateExercises, createdAt, updatedAt: createdAt });
    }
    if (newDefinitions.length) await db.exercises.bulkAdd(newDefinitions);
    await db.templates.bulkAdd(templates);
    await db.programs.toCollection().modify({ isCurrent: false });
    await db.programs.add({ id: programId, name: file.program.name, templateIds: templates.map((item) => item.id), archived: false, isCurrent: true, createdAt, updatedAt: createdAt });
    await db.settings.update("settings", { onboardingComplete: true });
    const metadata: ProgramImportMetadata = {
      id: makeId("import"), importedAt: createdAt, source: "import", fileName, schemaVersion: 1,
      previousTemplateVersionIds: [],
    };
    await db.imports.add(metadata);
  });
}

export async function exportProgram(): Promise<WorkoutProgramFile> {
  const program = await db.programs.filter((item) => item.isCurrent && !item.archived).first();
  if (!program) throw new Error("Aucun programme actif.");
  const templates = await db.templates.bulkGet(program.templateIds);
  const definitions = await db.exercises.toArray();
  const definitionMap = new Map(definitions.map((item) => [item.id, item]));
  return {
    format: "workout-program", schemaVersion: 1,
    program: {
      name: program.name,
      sessions: templates.filter(Boolean).map((template) => ({
        name: template!.name,
        exercises: template!.exercises.map((item) => {
          const definition = definitionMap.get(item.exerciseDefinitionId)!;
          return {
            exerciseId: definition.id, sourceExerciseId: definition.sourceExerciseId ?? definition.id,
            name: definition.name, trackingMode: definition.trackingMode, minSets: item.minSetCount, sets: item.setCount,
            repRange: item.minReps != null ? { min: item.minReps, max: item.maxReps! } : undefined,
            durationRange: item.minDuration != null ? { min: item.minDuration, max: item.maxDuration! } : undefined,
            rir: item.targetRirMin != null ? { min: item.targetRirMin, max: item.targetRirMax! } : undefined,
            restSeconds: item.restSeconds, unilateral: definition.isUnilateral, perSide: definition.perSide,
            load: item.load, unit: item.unit, notes: item.notes,
            progression: { type: definition.progressionMode, increment: item.loadIncrement, unit: item.unit, steps: item.discreteSteps, currentStep: item.discreteStepIndex },
          };
        }),
      })),
    },
  };
}
